import { useFlag } from '@/components/posthog-provider';
import { logError } from '@/lib/log-error';
import type {
  ConfirmationSelection,
  VideoFootageSelection,
} from '@borradh-workspace/contracts/claire';
import type { videoFootageRequiredSchema } from '@borradh-workspace/contracts/claire';
import { STOCK_FOOTAGE_MATCHING_NEW_FLAG } from '@borradh-workspace/labels';
import { useRef, useState } from 'react';
import type { z } from 'zod';
import { footageRecoveryMessage } from '../card-reply-display';
import {
  ComposerDecision,
  useCurrentComposerTurn,
  useDecisionActionsDisabled,
} from '../composer-gate-context';
import { ClipPickerDialog } from './clip-picker-dialog';
import { DecisionFooter } from './decision-footer';

const EMPTY_CLIP_IDS: string[] = [];

type Presentation = z.infer<typeof videoFootageRequiredSchema>;

/** Selecting sources retries creation. Opening or cancelling never creates a draft. */
export function VideoFootageRequiredCard({
  id,
  presentation,
  onSendMessage,
}: {
  id: string;
  presentation: Presentation;
  onSendMessage: (
    message: string,
    selection?: ConfirmationSelection,
    footageSelection?: VideoFootageSelection
  ) => boolean | void | Promise<boolean> | Promise<void>;
}) {
  const v2Enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  const [open, setOpen] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const sending = useRef(false);
  const currentTurn = useCurrentComposerTurn();
  const actionsDisabled = useDecisionActionsDisabled();
  const disabled = pending || actionsDisabled || !currentTurn;
  const send = async (
    message: string,
    footageSelection?: VideoFootageSelection
  ) => {
    if (!v2Enabled || sending.current || answered || disabled) return;
    sending.current = true;
    setPending(true);
    setError(false);
    try {
      const accepted = await (footageSelection
        ? onSendMessage(message, undefined, footageSelection)
        : onSendMessage(message));
      if (accepted !== false) setAnswered(true);
      else setError(true);
    } catch (error) {
      logError('assistant.video-footage.send', error, { feature: 'assistant' });
      setError(true);
    } finally {
      sending.current = false;
      setPending(false);
    }
  };
  const cancel = () =>
    void send('Cancel this video request. Do not create a video.');
  const choose = () => {
    if (!disabled && !answered) setOpen(true);
  };
  const confirm = (ids: string[]) => {
    if (!ids.length) return;
    void send(`Use these ${ids.length} clips for the video.`, {
      toolCallId: id,
      assetIds: ids,
    });
  };
  const decision = {
    sentence: 'Choose clips for your video.',
    acceptLabel: 'Choose clips',
    rejectLabel: 'Cancel',
    onAccept: choose,
    onReject: cancel,
    busy: disabled,
  };
  if (!v2Enabled || answered || !currentTurn) return null;
  return (
    <>
      <ComposerDecision id={id} gate={decision}>
        <div
          className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
          data-testid="video-footage-required-card"
        >
          <p className="text-sm font-medium">
            {presentation.title ?? 'More footage needed'}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            {presentation.title
              ? presentation.message
              : footageRecoveryMessage(presentation.message)}
          </p>
          <div className="mt-3">
            <DecisionFooter {...decision} hideSentence />
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-destructive">
              Your reply could not be sent. Try again.
            </p>
          )}
        </div>
      </ComposerDecision>
      {open && (
        <ClipPickerDialog
          open={open}
          onOpenChange={setOpen}
          selectedIds={presentation.selectedAssetIds ?? EMPTY_CLIP_IDS}
          onConfirm={confirm}
          serviceId={presentation.request.serviceId}
          maxCount={10}
        />
      )}
    </>
  );
}
